import {
    Select,
    SelectContent,
    SelectItem,
    SelectItemIndicator,
    SelectItemLabel,
    SelectItemLeading,
    SelectList,
    SelectTrigger,
} from '@plato/ui/components/select';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    type TooltipContentProps,
} from '@plato/ui/primitives/tooltip';
import { cn } from '@plato/ui/util/classes';
import { ChevronsUpDown } from 'lucide-react';
import { Text } from '../ui/text.tsx';
import { badgeVariants, type BadgeProps } from './badge.tsx';

type BadgeColor = NonNullable<BadgeProps['color']>;

const dotByColor: Record<BadgeColor, string> = {
    default: 'bg-green-500',
    green: 'bg-green-500',
    red: 'bg-red-500',
    neutral: 'bg-neutral-600',
    yellow: 'bg-orange-500',
    orange: 'bg-orange-500',
    blue: 'bg-blue-500',
};

export type SelectBadgeOption<T extends string> = {
    value: T;
    label: string;
    color?: BadgeColor;
};

export type SelectBadgeProps<T extends string> = {
    value: T;
    onChange: (next: T) => void;
    options: readonly SelectBadgeOption<T>[];
    // Override the trigger label/color when the badge needs to display
    // something different from the currently-checked option — e.g. an
    // "at risk" status when menu only offers Active/Inactive transitions.
    triggerLabel?: string;
    triggerColor?: BadgeColor;
    description?: string;
    descriptionSide?: TooltipContentProps['side'];
    className?: string;
    /** Test hook: every select trigger looks alike to a role query. */
    testId?: string;
};

function SelectBadge<T extends string>({
    value,
    onChange,
    options,
    triggerLabel,
    triggerColor,
    description,
    descriptionSide,
    className,
    testId,
}: SelectBadgeProps<T>): React.ReactNode {
    const selected = options.find((o) => o.value === value);
    const label = triggerLabel ?? selected?.label;
    const color = triggerColor ?? selected?.color ?? 'default';

    if (label === undefined) {
        return null;
    }

    const trigger = (
        <SelectTrigger
            aria-label={description ?? label}
            data-testid={testId}
            render={
                <button
                    type="button"
                    className={cn(
                        badgeVariants({ color }),
                        'w-auto justify-start cursor-pointer focus:outline-hidden focus:ring-0',
                        className,
                    )}
                />
            }
        >
            {label}
            <ChevronsUpDown className="h-3 w-3 -mx-0.5 shrink-0" />
        </SelectTrigger>
    );

    return (
        <Select
            title={description ?? label}
            items={options}
            value={value}
            onValueChange={(option) => {
                if (option) {
                    onChange(option);
                }
            }}
        >
            {description ? (
                <Tooltip>
                    <TooltipTrigger render={trigger} />
                    <TooltipContent side={descriptionSide}>{description}</TooltipContent>
                </Tooltip>
            ) : (
                trigger
            )}
            <SelectContent>
                <SelectList>
                    {(option: SelectBadgeOption<T>) => (
                        <SelectItem key={option.value} value={option}>
                            <SelectItemLeading>
                                <span
                                    className={cn(
                                        'h-1.5 w-1.5 shrink-0 rounded-full',
                                        dotByColor[option.color ?? 'default'],
                                    )}
                                />
                            </SelectItemLeading>
                            <SelectItemLabel>
                                <Text>{option.label}</Text>
                            </SelectItemLabel>
                            <SelectItemIndicator />
                        </SelectItem>
                    )}
                </SelectList>
            </SelectContent>
        </Select>
    );
}

export { SelectBadge };
